import { apiClient } from './api-client';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '@/lib/tokenStorage';
import type { User, AuthResponse } from '@centraoneplus/shared-types';
import { sendWithEncryptedPasswords } from '@/lib/credentialEncryption';

export interface LoginDto {
  email: string;
  password: string;
}

export interface RegisterDto {
  email: string;
  password: string;
  name: string;
  tenantSlug?: string;
}

// Login step 1 returns this challenge (mandatory email OTP) instead of tokens.
// The code is never sent to the browser — only where it went and how long it
// is valid.
export interface MfaChallenge {
  requiresMfa: true;
  mfaToken: string;
  maskedEmail: string;
  expiresInSeconds: number;
}

export type LoginResult = AuthResponse | MfaChallenge;

// Result of asking whether an email address should sign in via SSO. loginUrl is
// absolute and points at auth-service (the IdP posts its assertion there, not
// to the BFF), so it is followed with a full page navigation.
export interface SsoDiscovery {
  ssoEnabled: boolean;
  loginUrl?: string;
}

// Start of "Sign in with Microsoft". authorizeUrl points at Microsoft Entra ID
// and is followed with a full page navigation — every screen from there until
// the callback belongs to Microsoft.
export interface MicrosoftLoginStart {
  configured: boolean;
  authorizeUrl?: string;
}

export function isMfaChallenge(result: LoginResult): result is MfaChallenge {
  return (result as MfaChallenge).requiresMfa === true;
}

class AuthService {
  private readonly basePath = '/auth';

  // Step 1: email + password. Never yields tokens directly — returns an OTP
  // challenge (tokens are minted at verifyLoginOtp). Tokens are only persisted
  // when a full AuthResponse comes back (defensive; the no-MFA path is gone).
  async login(dto: LoginDto): Promise<LoginResult> {
    const response = await sendWithEncryptedPasswords(dto, (body) =>
      apiClient.post<LoginResult>(`${this.basePath}/login`, body),
    );
    const data = response.data;
    if (!isMfaChallenge(data)) {
      setTokens(data.accessToken, data.refreshToken);
    }
    return data;
  }

  // --- Microsoft SSO ------------------------------------------------------
  // Ask the backend for the Entra authorization URL. Never rejects: on any
  // failure the caller simply doesn't offer Microsoft sign-in.
  async getMicrosoftAuthorizeUrl(returnTo?: string): Promise<MicrosoftLoginStart> {
    try {
      const response = await apiClient.post<MicrosoftLoginStart>(
        `${this.basePath}/microsoft/login`,
        { returnTo },
      );
      return response.data ?? { configured: false };
    } catch {
      return { configured: false };
    }
  }

  // Whether to show the Microsoft button at all.
  async getMicrosoftStatus(): Promise<{ configured: boolean }> {
    try {
      const response = await apiClient.get<{ configured: boolean }>(
        `${this.basePath}/microsoft/status`,
      );
      return { configured: !!response.data?.configured };
    } catch {
      return { configured: false };
    }
  }

  // --- SAML SSO -----------------------------------------------------------
  // Ask whether this address belongs to an SSO-enabled tenant. Always resolves
  // (never rejects) so a discovery hiccup just leaves the user on the password
  // form instead of blocking the page.
  async discoverSso(email: string): Promise<SsoDiscovery> {
    try {
      const response = await apiClient.post<SsoDiscovery>(`${this.basePath}/sso/discover`, {
        email,
      });
      return response.data ?? { ssoEnabled: false };
    } catch {
      return { ssoEnabled: false };
    }
  }

  // Final leg: trade the single-use handoff code from the IdP redirect for a
  // real session. Persists the reference tokens exactly like password login.
  async exchangeSsoCode(code: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(`${this.basePath}/sso/exchange`, {
      code,
    });
    setTokens(response.data.accessToken, response.data.refreshToken);
    return response.data;
  }

  // Step 2: exchange the mfaToken + emailed code for real tokens.
  async verifyLoginOtp(mfaToken: string, code: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(`${this.basePath}/mfa/verify-login`, {
      mfaToken,
      code,
    });
    setTokens(response.data.accessToken, response.data.refreshToken);
    return response.data;
  }

  // Request a fresh code after the 90s window lapses.
  async resendLoginOtp(mfaToken: string): Promise<MfaChallenge> {
    const response = await apiClient.post<MfaChallenge>(`${this.basePath}/mfa/resend`, {
      mfaToken,
    });
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post(`${this.basePath}/logout`);
    } finally {
      clearTokens();
    }
  }

  async register(dto: RegisterDto): Promise<AuthResponse> {
    const response = await sendWithEncryptedPasswords(dto, (body) =>
      apiClient.post<AuthResponse>(`${this.basePath}/register`, body),
    );
    return response.data;
  }

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiClient.post<AuthResponse>(`${this.basePath}/refresh`, {
      refreshToken,
    });

    const { accessToken, refreshToken: newRefreshToken } = response.data;
    setTokens(accessToken, newRefreshToken);

    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>(`${this.basePath}/me`);
    return response.data;
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await apiClient.patch<User>(`${this.basePath}/me`, data);
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await sendWithEncryptedPasswords({ currentPassword, newPassword }, (body) =>
      apiClient.post(`${this.basePath}/change-password`, body),
    );
  }

  async requestPasswordReset(email: string): Promise<{ emailSent: boolean }> {
    const response = await apiClient.post<{ emailSent: boolean }>(
      `${this.basePath}/forgot-password`,
      { email },
    );
    // emailSent=false means the reset email could not be delivered (server-side
    // failure) — non-existent accounts still return true (anti-enumeration).
    return { emailSent: response.data?.emailSent !== false };
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await sendWithEncryptedPasswords({ token, newPassword }, (body) =>
      apiClient.post(`${this.basePath}/reset-password`, body),
    );
  }

  async listSessions(): Promise<ActiveSession[]> {
    const response = await apiClient.get<ActiveSession[]>(`${this.basePath}/sessions`);
    return response.data;
  }

  async revokeSession(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/sessions/${encodeURIComponent(id)}`);
  }

  async revokeOtherSessions(): Promise<{ revoked: number }> {
    const response = await apiClient.post<{ revoked: number }>(`${this.basePath}/sessions/revoke-others`);
    return response.data;
  }

  isAuthenticated(): boolean {
    return !!getAccessToken();
  }
}

/** One signed-in session of the current user (Settings > Security). */
export interface ActiveSession {
  id: string;
  current: boolean;
  startedAt: string | null;
  lastActiveAt: string;
  ipAddress: string | null;
  userAgent: string | null;
}

export const authService = new AuthService();
